import React, { FC, PropsWithChildren, useState } from 'react';

import { Outlet } from 'react-router-dom';
import styled from 'styled-components/macro';

import { CssBaseline, Paper as MuiPaper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { spacing } from '@mui/system';

import GlobalStyle from '../../components/GlobalStyle';
import NavBar from '../../components/NavBar';
import Sidebar from '../../components/sidebar/Sidebar';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Workspace = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const Drawer = styled.div<{ collapsed: boolean }>`
  width: ${(props) =>
    props.collapsed ? `${collapsedDrawerWidth}px` : `${drawerWidth}px`};
  flex-shrink: 0;
  overflow: hidden;
  background: #0f172a;
  transition: width 220ms ease;
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  background-color: #edf2f9 !important;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Layout: FC<PropsWithChildren> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const isDesktop = useMediaQuery('(min-width:1024px)');

  const handleDrawerToggle = () => {
    if (isDesktop) {
      setDesktopCollapsed((value) => !value);
    } else {
      setMobileOpen((value) => !value);
    }
  };

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <NavBar onDrawerToggle={isDesktop ? undefined : handleDrawerToggle} />
      <Workspace>
        {!isDesktop && (
          <Sidebar
            PaperProps={{
              style: {
                width: drawerWidth,
                top: 64,
                height: 'calc(100% - 64px)',
              },
            }}
            variant="temporary"
            open={mobileOpen}
            onClick={handleDrawerToggle}
          />
        )}
        {isDesktop && (
          <Drawer collapsed={desktopCollapsed}>
            <Sidebar
              collapsed={desktopCollapsed}
              PaperProps={{
                style: {
                  width: desktopCollapsed ? collapsedDrawerWidth : drawerWidth,
                  position: 'relative',
                  height: '100%',
                  transition: 'width 220ms ease',
                },
              }}
              variant="permanent"
              showNavigationToggle
              onNavigationToggle={handleDrawerToggle}
            />
          </Drawer>
        )}
        <AppContent>
          <MainContent p={{ xs: 4, sm: 6, md: 8 }}>
            {children}
            <Outlet />
          </MainContent>
        </AppContent>
      </Workspace>
    </Root>
  );
};

export default Layout;
